import React, { ReactElement, useEffect, useState, useRef } from 'react'

import { Box, Container, Drawer, Theme } from '@mui/material'

import NavigationItem from '../NavigationItem'
import Icon from '@components/core/media/Icon'
import MenuToggle from '@components/core/input/MenuToggle'
import { Link as GatsbyLink } from '@gatsbyjs/reach-router'
import Logo from '@components/core/media/Logo'

export type NavigationDrawerProps = {
  items: MCDC.Props.ILinkTo[]
  itemAccount: MCDC.Props.ILinkTo
  itemHome: MCDC.Props.ILinkTo
  activeIndex: number
  onClose: () => void
  isOpen: boolean
  sx?: MCDC.Props.IDefault['sx']
}

export default function NavigationDrawer({
  items,
  itemAccount,
  itemHome,
  activeIndex,
  onClose,
  isOpen,
  sx = {},
}: NavigationDrawerProps): ReactElement {
  return (
    <Drawer
      elevation={0}
      anchor="right"
      variant="temporary"
      open={isOpen}
      onClose={onClose}
      transitionDuration={500}
      hideBackdrop={true}
      PaperProps={{
        sx: [
          {
            width: '100vw',
            display: 'flex',
            height: '100%',
            backgroundColor: 'transparent',
          },
          sx,
        ],
      }}
      sx={[sx, { zIndex: (theme: Theme) => theme.zIndex.modal + 1 }]}
    >
      <Container
        maxWidth="xl"
        sx={[
          (theme: Theme) => ({
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            minHeight: 64,
            backgroundColor: 'transparent',
            zIndex: theme.zIndex.drawer + 1,
            px: `${theme.spacing(theme.gutter('sm'))} !important`,
            opacity: 0,
            '&:has(:focus-visible)': {
              opacity: 1,
            },
          }),
          sx,
        ]}
      >
        <GatsbyLink
          to={itemHome.url}
          title={itemHome.label as string}
          aria-label={itemHome.label as string}
          sx={{}}
        >
          <Logo />
        </GatsbyLink>
        <Box
          sx={{
            mr: -1,
          }}
        >
          <MenuToggle onClick={onClose} isToggled={isOpen} sx={{ mr: -1 }} />
        </Box>
      </Container>
      <Box
        component="ul"
        sx={(theme: Theme) => ({
          backgroundColor: theme.palette.grey[100],
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          p: 0,
          '> * + *': { mt: 6 },
          my: 0,
        })}
      >
        {items.map((entry, index) => (
          <NavigationItem
            to={entry.url}
            isExternal={entry.isExternal}
            count={entry.count}
            onClick={onClose}
            isSelected={activeIndex === index}
            key={index}
          >
            {entry.label}
          </NavigationItem>
        ))}
        <NavigationItem
          to={itemAccount.url}
          isExternal={itemAccount.isExternal}
          count={itemAccount.count}
          onClick={onClose}
          sx={{
            borderTop: '1px solid #d2d2d2',
            mt: 2,
            pt: 6,
          }}
        >
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Icon variant="favorite" sx={{ color: 'tertiary.main' }} />
            {itemAccount.label}
          </Box>
        </NavigationItem>
      </Box>
    </Drawer>
  )
}
